// Kurzlebige Effekte: Strahlen, Erz-Drops, Raketen, Ringe, Schadenszahlen.
//
// Alles laeuft ueber **Pools** (Gate FG-14). Ein neues Objekt je Partikel
// bedeutet Ruckler durch Garbage Collection — genau in den Momenten mit vielen
// Partikeln, also genau dann, wenn es auffaellt.
//
// Der Effektspeicher kennt die Szene nicht: er bekommt seine Rueckkopplung
// ueber zwei Rueckrufe (Erz eingesammelt, Rakete eingeschlagen). Das hielt
// schon im Vorlaeufer die Effekte testbar und verhinderte einen Importzyklus.

import { TAU, clamp } from '../core/math';
import { Pool } from '../core/pool';
import { rnd } from '../core/rng';
import type { GuidedTarget } from '../sim/guided';

export interface Beam {
  dead: boolean;
  x1: number; y1: number; x2: number; y2: number;
  color: number; width: number; life: number; max: number;
}

export interface Drop {
  dead: boolean;
  x: number; y: number; vx: number; vy: number;
  res: string; amount: number; gem: boolean;
  life: number; pulse: number; homing: boolean; r: number;
  /**
   * Reine Anzeige — der Server hat diese Beute bereits gebucht.
   *
   * Die zweite, **strukturelle** Sperre gegen Doppelbuchung: die erste ist
   * `store.authoritative`. Waere nur sie da, wuerde ein spaeterer Umbau der
   * Autoritaetsweiche das Erz still verdoppeln.
   */
  display: boolean;
}

export interface Missile {
  dead: boolean;
  x: number; y: number; vx: number; vy: number;
  angle: number; speed: number; damage: number; splash: number;
  /**
   * Woraufhin sie einlenkt — **Brocken oder Gegner**.
   *
   * Bis hierher war der Typ `Rock`, und damit suchte die Rakete ausschließlich
   * im Brockenfeld: sie flog an einem Gegner vorbei und traf ihn nur, wenn er
   * zufällig im Splash-Radius eines Brocken-Einschlags stand. Der Server buchte
   * ihren Anteil derweil voll gegen Gegner.
   */
  target: GuidedTarget | null; life: number; trail: number;
}

/**
 * Wie eine Lenkwaffe an ihr Ziel kommt — vom Aufrufer gestellt.
 *
 * Diese Datei kennt die Welt, aber **nicht** das Gegnerfeld, und soll es auch
 * nicht kennen: die Rangfolge „Gegner vor Brocken" ist eine Spielregel und steht
 * in `sim/guided.ts`, wo sie ohne Phaser prüfbar ist. Hier kommt sie als zwei
 * Funktionen herein — suchen und „lebt das noch?".
 */
/** Suchradius einer Rakete im Flug — unverändert gegenüber der Fassung davor. */
const MISSILE_SEEK = 420;

export interface MissileGuide {
  find(x: number, y: number, radius: number): GuidedTarget | null;
  alive(target: GuidedTarget): boolean;
}

/**
 * Ein Gegnerschuss — **reine Darstellung**.
 *
 * Er traegt bewusst **keinen Schaden**: der ist stetig verrechnet, in
 * `HostileField.step` und wortgleich im Server-Tick. Ein Geschoss, das selbst
 * buchte, waere eine zweite Simulation neben der des Servers — genau der
 * Fehler, den `Missile.damage` solo noch machen darf und online nicht (siehe
 * die Autoritaetsweiche in `SpaceScene`).
 */
export interface HostileShot {
  dead: boolean;
  x: number; y: number; vx: number; vy: number;
  angle: number; color: number; life: number;
  /** Frame im Geschossraster — jede Gegnerart feuert ihr eigenes Bild. */
  frame: number;
}

export interface Ring {
  dead: boolean;
  x: number; y: number; maxR: number; color: number; life: number; max: number;
}

export interface FloatText {
  dead: boolean;
  x: number; y: number; vx: number; vy: number;
  text: string; color: string; size: number; life: number; max: number;
}

export type CollectFn = (res: string, amount: number, gem: boolean, display: boolean) => void;
export type ExplodeFn = (x: number, y: number, damage: number, radius: number) => void;

// Obergrenzen der Effektarten.
//
// Der Pool schuetzt vor dem Garbage Collector, **nicht** vor der Menge: bei
// leerem Vorrat legt `spawn()` einfach neu an, es gibt also keinen natuerlichen
// Bremspunkt. Bei dichtem Feld — Nova, Splash, ein `harvest` mit bis zu 24
// Brocken, fuenfmal pro Sekunde — waechst die Zeichenlast sonst unbegrenzt.
//
// Das sind **Darstellungsgrenzen, keine Balancing-Knoepfe**: sie formen keine
// Entscheidung des Spielers, sondern nur eine Kurve, und stehen deshalb hier
// und nicht in der Tabelle.
export const MAX_TEXTS = 70;
export const MAX_BEAMS = 120;
export const MAX_RINGS = 40;
export const MAX_DROPS = 180;
export const MAX_SHOTS = 90;

export class Effects {
  readonly beams = new Pool<Beam>(() => ({
    dead: false, x1: 0, y1: 0, x2: 0, y2: 0, color: 0, width: 1, life: 0, max: 1,
  }));
  readonly drops = new Pool<Drop>(() => ({
    dead: false, x: 0, y: 0, vx: 0, vy: 0, res: '', amount: 0, gem: false,
    life: 0, pulse: 0, homing: false, r: 3.4, display: false,
  }));
  readonly missiles = new Pool<Missile>(() => ({
    dead: false, x: 0, y: 0, vx: 0, vy: 0, angle: 0, speed: 240,
    damage: 0, splash: 0, target: null, life: 0, trail: 0,
  }));
  readonly shots = new Pool<HostileShot>(() => ({
    dead: false, x: 0, y: 0, vx: 0, vy: 0, angle: 0, color: 0xffffff, life: 0, frame: 0,
  }));
  readonly rings = new Pool<Ring>(() => ({
    dead: false, x: 0, y: 0, maxR: 0, color: 0, life: 0, max: 1,
  }));
  readonly texts = new Pool<FloatText>(() => ({
    dead: false, x: 0, y: 0, vx: 0, vy: 0, text: '', color: '#fff', size: 11, life: 0, max: 1,
  }));

  /** Wiederverwendeter Zielpuffer der Raketen-Zielsuche (Gate FG-14). */

  clear(): void {
    this.beams.clear();
    this.drops.clear();
    this.missiles.clear();
    this.shots.clear();
    this.rings.clear();
    this.texts.clear();
  }

  /**
   * Ein Gegnerschuss auf einen Punkt. Er fliegt gerade und erlischt dort.
   *
   * Kein Zielobjekt und kein Treffertest: was er anrichtet, steht laengst fest.
   * Er zeigt es nur.
   */
  spawnHostileShot(
    x: number, y: number, zielX: number, zielY: number, speed: number, color: number, frame: number,
  ): void {
    if (this.shots.length >= MAX_SHOTS) return;
    const dx = zielX - x;
    const dy = zielY - y;
    const dist = Math.hypot(dx, dy) || 1;
    const shot = this.shots.spawn();
    shot.x = x; shot.y = y;
    shot.vx = (dx / dist) * speed;
    shot.vy = (dy / dist) * speed;
    shot.angle = Math.atan2(dy, dx);
    shot.color = color;
    shot.frame = frame;
    shot.life = Math.min(3, dist / speed);
  }

  spawnBeam(x1: number, y1: number, x2: number, y2: number, color: number, width = 1.6, life = 0.09): void {
    if (this.beams.length >= MAX_BEAMS) return;
    const beam = this.beams.spawn();
    beam.x1 = x1; beam.y1 = y1; beam.x2 = x2; beam.y2 = y2;
    beam.color = color; beam.width = width; beam.life = life; beam.max = life;
  }

  /**
   * Ein Erz-Drop — der einzige Effekt, der **nie** verworfen werden darf.
   *
   * Solo traegt er echte Beute: sie wird erst beim Einsammeln gebucht. Ihn am
   * Deckel wegzuwerfen hiesse, dem Spieler Erz zu nehmen, das er sich verdient
   * hat. Deshalb wird ueber dem Deckel **zusammengefasst** statt verworfen: der
   * Posten wandert auf einen gleichartigen Drop, der schon fliegt. Die Summe
   * bleibt exakt, nur die Zahl der Punkte auf dem Schirm nicht.
   */
  spawnDrop(x: number, y: number, res: string, amount: number, gem = false, display = false): void {
    if (this.drops.length >= MAX_DROPS && this.mergeDrop(res, amount, gem, display)) return;
    const drop = this.drops.spawn();
    const angle = rnd(TAU);
    const speed = rnd(30, 90);
    drop.x = x; drop.y = y;
    drop.vx = Math.cos(angle) * speed;
    drop.vy = Math.sin(angle) * speed;
    drop.res = res; drop.amount = amount; drop.gem = gem;
    drop.life = 26; drop.pulse = rnd(TAU); drop.homing = false;
    drop.r = gem ? 5 : 3.4;
    drop.display = display;
  }

  /** Einen gleichartigen Drop aufstocken. Gibt zurueck, ob es geklappt hat. */
  private mergeDrop(res: string, amount: number, gem: boolean, display: boolean): boolean {
    for (const drop of this.drops.active) {
      // `dead` heisst: schon eingesammelt und nur noch nicht aufgeraeumt. Wer
      // dorthin aufstockt, verschenkt den Posten.
      if (drop.dead) continue;
      if (drop.res === res && drop.gem === gem && drop.display === display) {
        drop.amount += amount;
        return true;
      }
    }
    return false;
  }

  spawnMissile(x: number, y: number, angle: number, damage: number, splash: number, speed = 240): void {
    const missile = this.missiles.spawn();
    missile.x = x; missile.y = y;
    missile.angle = angle;
    missile.vx = Math.cos(angle) * speed * 0.4;
    missile.vy = Math.sin(angle) * speed * 0.4;
    missile.speed = speed;
    missile.damage = damage; missile.splash = splash;
    missile.target = null; missile.life = 3.4; missile.trail = 0;
  }

  spawnRing(x: number, y: number, maxR: number, color: number, life = 0.5): void {
    if (this.rings.length >= MAX_RINGS) return;
    const ring = this.rings.spawn();
    ring.x = x; ring.y = y; ring.maxR = maxR; ring.color = color;
    ring.life = life; ring.max = life;
  }

  spawnText(x: number, y: number, text: string, color: string, size = 11): void {
    if (this.texts.length >= MAX_TEXTS) return;
    const item = this.texts.spawn();
    item.x = x + rnd(-6, 6); item.y = y;
    item.vx = rnd(-14, 14); item.vy = -34 - rnd(14);
    item.text = text; item.color = color; item.size = size;
    item.life = 0.85; item.max = 0.85;
  }

  /**
   * Ein Simulationsschritt der Effekte.
   *
   * @param onCollect wird beim Einsammeln eines Erz-Drops gerufen
   * @param onExplode wird beim Einschlag einer Rakete gerufen
   */
  update(
    dt: number,
    ship: { x: number; y: number },
    magnetRange: number,
    magnetSpeed: number,
    onCollect: CollectFn,
    onExplode: ExplodeFn,
    guide: MissileGuide,
  ): void {
    this.beams.forEach((beam) => { beam.life -= dt; if (beam.life <= 0) beam.dead = true; });
    this.rings.forEach((ring) => { ring.life -= dt; if (ring.life <= 0) ring.dead = true; });

    // Gegnerschuesse fliegen geradeaus und erloeschen. Kein Treffertest: der
    // Schaden ist stetig verrechnet, das Geschoss zeigt ihn nur.
    this.shots.forEach((shot) => {
      shot.life -= dt;
      if (shot.life <= 0) { shot.dead = true; return; }
      shot.x += shot.vx * dt;
      shot.y += shot.vy * dt;
    });

    this.texts.forEach((item) => {
      item.life -= dt;
      if (item.life <= 0) { item.dead = true; return; }
      item.x += item.vx * dt;
      item.y += item.vy * dt;
      item.vy += 40 * dt;
    });

    const range2 = magnetRange * magnetRange;
    this.drops.forEach((drop) => {
      drop.life -= dt;
      if (drop.life <= 0) { drop.dead = true; return; }
      const dx = ship.x - drop.x;
      const dy = ship.y - drop.y;
      const dist2 = dx * dx + dy * dy;
      if (drop.homing || dist2 < range2) {
        drop.homing = true;
        const dist = Math.sqrt(dist2) || 1;
        const pull = magnetSpeed * clamp(1.6 - dist / (magnetRange + 120), 0.35, 1.6);
        drop.vx += (dx / dist) * pull * dt * 6;
        drop.vy += (dy / dist) * pull * dt * 6;
        drop.vx *= 1 - 3 * dt; drop.vy *= 1 - 3 * dt;
        if (dist < 16) {
          drop.dead = true;
          onCollect(drop.res, drop.amount, drop.gem, drop.display);
          return;
        }
      } else {
        drop.vx *= 1 - 1.2 * dt;
        drop.vy *= 1 - 1.2 * dt;
      }
      drop.x += drop.vx * dt;
      drop.y += drop.vy * dt;
      drop.pulse += dt * 6;
    });

    this.missiles.forEach((missile) => {
      missile.life -= dt;
      if (missile.life <= 0) {
        missile.dead = true;
        onExplode(missile.x, missile.y, missile.damage, missile.splash);
        return;
      }
      // Verliert sie ihr Ziel — Gegner erledigt, Brocken zerlegt —, sucht sie
      // nach **derselben** Rangfolge neu.
      if (!missile.target || !guide.alive(missile.target)) {
        missile.target = guide.find(missile.x, missile.y, MISSILE_SEEK);
      }
      if (missile.target) {
        const want = Math.atan2(missile.target.y - missile.y, missile.target.x - missile.x);
        const diff = ((want - missile.angle + Math.PI * 3) % TAU) - Math.PI;
        missile.angle += clamp(diff, -6 * dt, 6 * dt);
      }
      const speed = Math.min(missile.speed, Math.hypot(missile.vx, missile.vy) + missile.speed * 2.6 * dt);
      missile.vx = Math.cos(missile.angle) * speed;
      missile.vy = Math.sin(missile.angle) * speed;
      missile.x += missile.vx * dt;
      missile.y += missile.vy * dt;

      if (missile.target && guide.alive(missile.target)) {
        const d2 = (missile.target.x - missile.x) ** 2 + (missile.target.y - missile.y) ** 2;
        if (d2 < (missile.target.r + 8) ** 2) {
          missile.dead = true;
          onExplode(missile.x, missile.y, missile.damage, missile.splash);
        }
      }
    });

    this.beams.sweep();
    this.rings.sweep();
    this.texts.sweep();
    this.drops.sweep();
    this.missiles.sweep();
  }
}
